import CloseIcon from '@mui/icons-material/Close';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import React, { useState } from 'react';
import { useAuthContext } from '../context/AuthContext';
import { useNotificationContext } from '../context/NotificationContext';
import { ApiService } from '../services/api';
import { IssueReasonValue, ISSUE_REASONS, ReportIssueSource } from '../types';
import { StoreDateTime } from './StoreDateTime';

interface ReportIssueButtonProps {
  source: ReportIssueSource;
  imageId: string | null;
  imageUrl: string | null;
  modTagOptions?: string[];
  className?: string;
  storeName?: string | null;
  deviceName?: string | null;
  cameraId?: string | null;
  detectedAt?: string | null;
  storeTimeZone?: string;
}

function stripImageUrlToken(url: string): string {
  const queryIndex = url.indexOf('?');
  return queryIndex === -1 ? url : url.slice(0, queryIndex);
}

export const ReportIssueButton: React.FC<ReportIssueButtonProps> = ({
  source,
  imageId,
  imageUrl,
  modTagOptions,
  className,
  storeName,
  deviceName,
  cameraId,
  detectedAt,
  storeTimeZone,
}) => {
  const { token, hasInternalAccess } = useAuthContext();
  const { showSuccessToast, showErrorToast } = useNotificationContext();

  const [isOpen, setIsOpen] = useState(false);
  const [reason, setReason] = useState<IssueReasonValue | ''>('');
  const [modTag, setModTag] = useState<string>('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const mods = modTagOptions ?? [];

  const openDialog = () => setIsOpen(true);

  const closeDialog = () => {
    if (submitting) return;
    setIsOpen(false);
    setReason('');
    setModTag('');
    setNote('');
  };

  const handleReasonChange = (event: SelectChangeEvent) => {
    setReason(event.target.value as IssueReasonValue);
  };

  const handleSubmit = async () => {
    if (!reason || !imageId || !imageUrl) return;
    const numericImageId = Number(imageId);
    if (!Number.isFinite(numericImageId)) {
      showErrorToast('Cannot report: image ID is not numeric');
      return;
    }
    setSubmitting(true);
    try {
      await ApiService.reportIssue(token, {
        image_id: numericImageId,
        image_url: stripImageUrlToken(imageUrl),
        mod_tag: modTag || null,
        reason,
        note: note.trim() || undefined,
        source,
      });
      showSuccessToast('Issue reported');
      setIsOpen(false);
      setReason('');
      setModTag('');
      setNote('');
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Error reporting issue');
    } finally {
      setSubmitting(false);
    }
  };

  if (!hasInternalAccess) return null;

  if (!imageId || !imageUrl) return null;

  return (
    <>
      <button
        onClick={openDialog}
        className={
          className ?? 'px-4 py-2 text-red-600 hover:bg-red-50 rounded-md flex items-center gap-1'
        }
      >
        <ReportProblemOutlinedIcon fontSize="small" />
        Report Issue
      </button>

      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={closeDialog}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-bold">Report Issue</h2>
              <button onClick={closeDialog} className="p-1 hover:bg-gray-100 rounded-full">
                <CloseIcon />
              </button>
            </div>

            <div className="px-6 pt-4 text-sm text-gray-600 space-y-1">
              {storeName && (
                <div>
                  <span className="text-gray-400">Store:</span> {storeName}
                </div>
              )}
              {(deviceName || cameraId) && (
                <div>
                  <span className="text-gray-400">Camera:</span> {deviceName ?? cameraId}
                  {deviceName && cameraId ? ` (${cameraId})` : ''}
                </div>
              )}
              {imageId && (
                <div>
                  <span className="text-gray-400">Image ID:</span> {imageId}
                </div>
              )}
              {detectedAt && (
                <div>
                  <span className="text-gray-400">Captured:</span>{' '}
                  <StoreDateTime value={detectedAt} storeTimeZone={storeTimeZone} />
                </div>
              )}
            </div>

            <div className="px-6 py-4 space-y-4">
              <FormControl size="small" fullWidth>
                <InputLabel size="small" id="report-issue-reason-label">
                  Reason
                </InputLabel>
                <Select
                  labelId="report-issue-reason-label"
                  value={reason}
                  size="small"
                  label="Reason"
                  onChange={handleReasonChange}
                >
                  {ISSUE_REASONS.map((r) => (
                    <MenuItem key={r.value} value={r.value}>
                      {r.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {mods.length > 0 && (
                <FormControl size="small" fullWidth>
                  <InputLabel size="small" id="report-issue-mod-label">
                    Mod (optional)
                  </InputLabel>
                  <Select
                    labelId="report-issue-mod-label"
                    value={modTag}
                    size="small"
                    label="Mod (optional)"
                    onChange={(event) => setModTag(event.target.value)}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {mods.map((tag) => (
                      <MenuItem key={tag} value={tag}>
                        {tag}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Additional details (optional)"
                rows={3}
                maxLength={1024}
                className="w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex justify-end gap-2 px-6 py-4 border-t">
              <button
                onClick={closeDialog}
                disabled={submitting}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 text-sm disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!reason || submitting}
                className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm font-medium disabled:opacity-50"
              >
                {submitting ? 'Submitting...' : 'Submit Report'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
